import {
  BarChart3,
  BookOpen,
  Building2,
  CircleUserRound,
  FilePlus2,
  Inbox,
  LayoutDashboard,
  MonitorCog,
  LogOut,
  Search,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
};

const agentItems: NavItem[] = [
  { to: "/desk", label: "Início", icon: LayoutDashboard, end: true },
  { to: "/desk/chamados", label: "Chamados", icon: Inbox },
  { to: "/desk/novo", label: "Novo chamado", icon: FilePlus2 },
  { to: "/desk/clientes", label: "Clientes", icon: UsersRound },
  { to: "/desk/organizacoes", label: "Organizações", icon: Building2 },
  { to: "/desk/equipamentos", label: "Equipamentos", icon: MonitorCog },
  { to: "/desk/base", label: "Base de conhecimento", icon: BookOpen },
  { to: "/desk/relatorios", label: "Relatórios", icon: BarChart3 },
];

const clientItems: NavItem[] = [
  { to: "/", label: "Início", icon: LayoutDashboard, end: true },
  { to: "/novo", label: "Novo chamado", icon: FilePlus2 },
  { to: "/chamados", label: "Meus chamados", icon: Inbox },
  { to: "/conta", label: "Minha conta", icon: CircleUserRound },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, logout } = useAuth();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const isAgent = location.pathname.startsWith("/desk");
  const items = isAgent ? agentItems : clientItems;
  const user = session?.user;

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = search.trim();
    if (!query) {
      searchRef.current?.focus();
      return;
    }

    const base = isAgent ? "/desk/chamados" : "/chamados";
    navigate(base + "?q=" + encodeURIComponent(query));
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <img className="brand-logo brand-logo-negative" src="/brand/noverik-logo-mono.png" alt="Noverik Tecnologia" />
          <span className="brand-product">SERVICE DESK</span>
        </div>

        <nav className="sidebar-nav" aria-label="Navegação principal">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}
            >
              <Icon size={18} strokeWidth={1.9} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <span className="status-dot" />
          <div>
            <strong>Central online</strong>
            <small>Serviços operacionais</small>
          </div>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <form className="searchbox" onSubmit={submitSearch}>
            <Search size={18} />
            <input
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={isAgent ? "Buscar chamados..." : "Buscar meus chamados..."}
            />
            <kbd>Ctrl K</kbd>
          </form>

          <div className="account">
            <div className="avatar">{initials(user?.name || "Noverik")}</div>
            <div className="account-copy">
              <strong>{user?.name || "Usuário"}</strong>
              <span>
                {user?.role === "client"
                  ? user.organization?.name || "Cliente"
                  : user?.role === "admin"
                    ? "Administrador Noverik"
                    : "Equipe Noverik"}
              </span>
            </div>
            <button
              className="icon-button"
              aria-label="Sair"
              title="Sair"
              onClick={() => void logout()}
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </section>
    </div>
  );
}
